import React, { useState } from "react";
import styled from "styled-components";
import {
  deleteMedia,
  uploadMedia,
} from "../../database/media/cloudflareMedia";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

const UploadLabel = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-4);
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  transition:
    background var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard);
  align-self: flex-start;

  &:hover {
    background: ${(p) =>
      p.$disabled ? "var(--color-surface)" : "var(--color-surface-subtle)"};
    border-color: ${(p) =>
      p.$disabled ? "var(--color-border)" : "var(--color-border-strong)"};
  }
`;

const Preview = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-2);
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    max-height: 200px;
    object-fit: cover;
    border-radius: var(--radius-sm);
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ds-image-upload-spin 0.6s linear infinite;

  @keyframes ds-image-upload-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export default ({ field, form, actionId, reference }) => {
  const [uploading, setUploading] = useState(false);

  const deleteOldImage = async (url) => {
    if (url) {
      try {
        await deleteMedia({ url });
      } catch (error) {
        console.error("Error deleting old image:", error);
      }
    }
  };

  const handleImageChange = async (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setUploading(true);
      try {
        const previousUrl = field.value;
        const uploaded = await uploadMedia({
          body: file,
          kind: "image",
          fileName: file.name,
          firebasePath: `${reference}/${actionId}/${file.name}`,
        });
        form.setFieldValue(field.name, uploaded.url);
        if (previousUrl && previousUrl !== uploaded.url) {
          void deleteOldImage(previousUrl);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setUploading(false);
      }
    }
  };

  const inputId = `image-upload-${field.name}`;

  return (
    <Wrapper>
      <HiddenInput
        accept="image/*"
        id={inputId}
        type="file"
        onChange={handleImageChange}
        disabled={uploading}
      />
      <UploadLabel htmlFor={inputId} $disabled={uploading}>
        {uploading ? (
          <>
            <Spinner /> Uploading…
          </>
        ) : (
          <>
            <UploadIcon /> Upload image
          </>
        )}
      </UploadLabel>
      {field.value && (
        <Preview>
          <img src={field.value} alt="Preview" />
        </Preview>
      )}
    </Wrapper>
  );
};
